// Frontend source of truth for body emphasis grid options (characterForm UI, tag helpers).
// If you change BODY_OPTION_CATALOG, also update BODY_CATALOG_STRING in
// WORKERS/wrangler-edits/replicate-image-generator/utils/prompts.js (worker keeps a separate copy).

export const DEFAULT_BODY_OPTION_ID = 5;

export const BODY_OPTION_CATALOG = {
  female: [
    { id: 1, label: 'loli', promptTag: 'loli, flat chest,', hiddenFromPicker: true },
    { id: 2, label: 'oppai loli', promptTag: '(oppai loli, large breasts:0.9),', hiddenFromPicker: true },
    { id: 3, label: 'petite, small breasts', promptTag: 'petite, small breasts,' },
    { id: 4, label: 'petite, medium breasts', promptTag: 'petite, medium breasts,' },
    { id: 5, label: 'petite, large breasts', promptTag: 'petite, large breasts,' },
    { id: 6, label: 'skinny, medium breasts', promptTag: 'skinny, medium breasts,' },
    { id: 7, label: 'skinny, medium breasts, curvy', promptTag: 'skinny, medium breasts, curvy,' },
    { id: 8, label: 'wide hips, narrow waist, medium breasts', promptTag: 'wide hips, narrow waist, medium breasts,' },
    { id: 10, label: 'wide hips, narrow waist, large breasts', promptTag: 'wide hips, narrow waist, large breasts,' },
    { id: 12, label: 'curvy, large breasts', promptTag: 'curvy, large breasts,' },
    { id: 13, label: 'curvy, huge breasts', promptTag: 'curvy, huge breasts,' },
  ],
  male: [
    { id: 1, label: 'shota', promptTag: '(shota:0.5),', hiddenFromPicker: true },
    { id: 2, label: 'trap', promptTag: '(trap:0.7),', hiddenFromPicker: true },
    { id: 3, label: 'slender', promptTag: 'slender,' },
    { id: 4, label: 'toned', promptTag: '(toned:0.7),' },
    { id: 5, label: 'muscular', promptTag: 'muscular,' },
    { id: 6, label: 'tall male', promptTag: 'tall male,' },
    { id: 7, label: 'mature male', promptTag: 'mature male,', hiddenFromPicker: true },
  ],
};

export const BUST_KEYWORDS = ['flat chest', 'small breasts', 'medium breasts', 'large breasts', 'huge breasts'];
export const FEMALE_BODY_KEYWORDS = [
  'petite', 'skinny', 'thin body', 'curvy', 'oppai loli', 'loli',
  'wide hips', 'narrow waist',
];
export const MALE_BODY_KEYWORDS = [
  'skinny', 'thin body', 'slender', 'muscular', 'toned body', 'athletic build',
  'trap', 'shota', 'tall male', 'mature male',
];
export const FEMALE_BODY_TYPE_WARNING_TAGS = [
  'loli', 'petite', 'skinny', 'wide hips', 'narrow waist', 'curvy',
];
export const MALE_BODY_TYPE_WARNING_TAGS = ['shota', 'slender', 'toned', 'muscular'];

/** Split comma/newline-delimited prompt tags without breaking parentheses groups. */
export const splitPromptTagsAware = (promptText) => {
  const text = String(promptText || '');
  const tokens = [];
  let current = '';
  let depth = 0;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === '(') {
      depth += 1;
      current += ch;
      continue;
    }
    if (ch === ')') {
      depth = Math.max(0, depth - 1);
      current += ch;
      continue;
    }
    if ((ch === ',' || ch === '\n') && depth === 0) {
      const trimmed = current.trim();
      if (trimmed) tokens.push(trimmed);
      current = '';
      continue;
    }
    current += ch;
  }

  const trimmed = current.trim();
  if (trimmed) tokens.push(trimmed);
  return tokens;
};

export const BODY_PROMPT_TAG_TOKEN_KEYS = (() => {
  const tokenKeys = new Set();
  Object.values(BODY_OPTION_CATALOG).forEach((options) => {
    options.forEach((option) => {
      splitPromptTagsAware(option.promptTag || '')
        .forEach((token) => tokenKeys.add(token.toLowerCase().replace(/\s+/g, ' ')));
    });
  });
  return tokenKeys;
})();

const isAttentionInnerBalanced = (value) => {
  let parenDepth = 0;
  let bracketDepth = 0;

  for (const ch of value) {
    if (ch === '(') {
      parenDepth += 1;
      continue;
    }
    if (ch === ')') {
      parenDepth -= 1;
      if (parenDepth < 0) return false;
      continue;
    }
    if (ch === '[') {
      bracketDepth += 1;
      continue;
    }
    if (ch === ']') {
      bracketDepth -= 1;
      if (bracketDepth < 0) return false;
    }
  }

  return parenDepth === 0 && bracketDepth === 0;
};

/** Peel one outer SD attention layer: (tag:0.8), ((tag)), [tag:0.8]. */
const peelAttentionOnce = (tagName) => {
  const isParenWrapped = tagName.startsWith('(') && tagName.endsWith(')');
  const isBracketWrapped = tagName.startsWith('[') && tagName.endsWith(']');
  if (!isParenWrapped && !isBracketWrapped) return tagName;

  const inner = tagName.slice(1, -1);
  const weightMatch = inner.match(/^(.*):([\d.]+)$/);
  if (weightMatch && isAttentionInnerBalanced(weightMatch[1])) {
    return weightMatch[1].trim();
  }

  return inner.trim();
};

/** Strip all outer attention/weight wrappers from a prompt token. */
export const stripAttentionWeight = (token) => {
  let current = String(token || '').trim();
  if (!current) return '';

  for (;;) {
    const next = peelAttentionOnce(current);
    if (next === current) break;
    current = next;
  }

  return current;
};

const toPromptTokenKey = (token) =>
  String(token || '')
    .replace(/,+\s*$/, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

/**
 * Split prompt text into weight-insensitive token keys.
 * Peels (tag:1.2) / [tag] wrappers, then re-splits comma groups revealed by peeling.
 */
export const normalizeBodyPromptTokenKeys = (text) => {
  const keys = [];
  splitPromptTagsAware(text).forEach((token) => {
    const peeled = stripAttentionWeight(token);
    if (!peeled) return;

    const parts = peeled.includes(',') ? splitPromptTagsAware(peeled) : [peeled];
    parts.forEach((part) => {
      const key = toPromptTokenKey(part);
      if (key) keys.push(key);
    });
  });
  return keys;
};

const LOCATION_DENYLIST = [
  'outdoors', 'indoors', 'classroom', 'bedroom', 'kitchen', 'bathroom',
  'city', 'forest', 'beach', 'park', 'street', 'office', 'school',
  'library', 'hospital', 'castle', 'garden', 'rooftop', 'night sky',
  'simple background', 'white background',
];

export const normalizeGenderKey = (gender) => {
  const g = String(gender || '').toLowerCase().trim();
  return g === 'male' ? 'male' : 'female';
};

export const isValidBodyOptionId = (gender, optionId) => {
  const normalizedGender = normalizeGenderKey(gender);
  const id = parseInt(optionId, 10);
  if (Number.isNaN(id)) return false;
  return BODY_OPTION_CATALOG[normalizedGender]?.some((o) => o.id === id) ?? false;
};

export const isOfferedBodyOptionId = (gender, optionId) => {
  const normalizedGender = normalizeGenderKey(gender);
  const id = parseInt(optionId, 10);
  if (Number.isNaN(id)) return false;
  const option = BODY_OPTION_CATALOG[normalizedGender]?.find((o) => o.id === id);
  return Boolean(option && !option.hiddenFromPicker);
};

export const resolveBodyOptionId = (gender, optionId) => {
  if (isOfferedBodyOptionId(gender, optionId)) return parseInt(optionId, 10);
  return DEFAULT_BODY_OPTION_ID;
};

export const getBodyOptionById = (gender, optionId) => {
  const normalizedGender = normalizeGenderKey(gender);
  const id = resolveBodyOptionId(normalizedGender, optionId);
  return BODY_OPTION_CATALOG[normalizedGender]?.find((o) => o.id === id) ?? null;
};

export const isBodyPromptToken = (tag) => {
  const normalized = String(tag || '').toLowerCase().replace(/\s+/g, ' ').trim();
  if (!normalized) return false;
  if (BODY_PROMPT_TAG_TOKEN_KEYS.has(normalized)) return true;

  const cleanTag = normalized.replace(/[():\d.]/g, '').trim();
  const bodyKeywords = [...BUST_KEYWORDS, ...FEMALE_BODY_KEYWORDS, ...MALE_BODY_KEYWORDS];
  return bodyKeywords.some((kw) => cleanTag.includes(kw.toLowerCase()));
};

export const isLocationDenylistTag = (tag) => {
  const normalized = String(tag || '').toLowerCase().replace(/_/g, ' ').trim();
  if (!normalized) return false;
  return LOCATION_DENYLIST.some((loc) => normalized === loc || normalized.includes(loc));
};
